import { Base, Flex, variables } from '@bitrise/bitkit';
import {
  SeriesPointXY,
  useGetNearestPoint,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGLine,
} from '@bitrise/bitviz';
import { BuildMetricsTimeSeries } from '@trace/types';
import { DurationUnit } from 'luxon';
import React, { FunctionComponent, PointerEvent, useEffect, useState } from 'react';
import max from 'lodash.max';
import MessageFailing from '../../ViewVisualisations/MessageFailing';
import MessageFetching from '../../ViewVisualisations/MessageFetching';
import { BUILD_METRIC_COLORS_HEX } from '../mappings';
import BuildTimeSelector from './BuildTimeSelector';
import BuildTooltip from '../BuildTooltip';
import BuildTimesByStatusTooltipContent from './BuildTimesByStatusTooltipContent';

export interface Props {
  dataOverTime: BuildMetricsTimeSeries[];
  dateLeft?: number;
  dateRight?: number;
  height?: number;
  isError: boolean;
  isFetching: boolean;
  onDateRangeChange?: (left: number, right: number) => void;
  refetch: () => void;
  step: DurationUnit;
  stepSize: number;
  ticks: number[];
  withBackground?: boolean;
  withTimeSelector?: boolean;
}

export interface GetSeriesResult {
  series: [SeriesPointXY[], SeriesPointXY[], SeriesPointXY[], SeriesPointXY[]];
  seriesMax: SeriesPointXY[];
  yMin: number;
  yMax: number;
}

export const metrics: [
  keyof BuildMetricsTimeSeries,
  keyof BuildMetricsTimeSeries,
  keyof BuildMetricsTimeSeries,
  keyof BuildMetricsTimeSeries,
] = ['totalTimeWait', 'totalTimeAbortManual', 'totalTimeFail', 'totalTimeSuccess'];

const getPreviousValue = (
  series: GetSeriesResult['series'],
  i: number,
  x: number | null,
): number | null => {
  if (x !== null) {
    for (let j = i + 1; j < series.length; j += 1) {
      if (series[j] && series[j][x] && series[j][x].y) {
        return series[j][x].y;
      }
    }
  }

  return null;
};

const isLastValue = (series: GetSeriesResult['series'], i: number, x: number | null): boolean => {
  if (x !== null) {
    for (let j = i - 1; j >= 0; j -= 1) {
      if (series[j] && series[j][x] && series[j][x].y) {
        return false;
      }
    }
  }

  return true;
};

const getSeries = (data?: BuildMetricsTimeSeries): GetSeriesResult => {
  const series: GetSeriesResult['series'] = [[], [], [], []];
  const seriesMax: GetSeriesResult['seriesMax'] = [];
  let yMax = max(data?.totalTime);

  if (!data || yMax === undefined) {
    return {
      series: [[], [], [], []],
      seriesMax: [],
      yMax: 1000,
      yMin: 0,
    };
  }

  let yMin = yMax;

  for (let i = metrics.length - 1; i >= 0; i -= 1) {
    for (let x = 0; x < data[metrics[i]].length; x += 1) {
      const start = getPreviousValue(series, i, x) || 0;
      const y = data[metrics[i]][x] ? start + data[metrics[i]][x] : null;

      if (y !== null) {
        if (y < yMin) yMin = y;
        if (y > yMax) yMax = y;

        if (!seriesMax[x] || seriesMax[x].y === null || seriesMax[x].y < y) {
          seriesMax[x] = { x, y };
        }
      }

      series[i][x] = { x, y };
    }
  }

  return {
    series,
    seriesMax,
    yMin: 0,
    yMax: yMax * 1.1,
  };
};

const BuildTimesByStatus: FunctionComponent<Props> = ({
  ticks,
  dateLeft = ticks[0],
  dateRight = ticks[ticks.length - 1],
  dataOverTime,
  height = 200,
  isError,
  isFetching,
  onDateRangeChange,
  refetch,
  step,
  stepSize,
  withBackground,
  withTimeSelector,
}) => {
  const [focusPoint, setFocusPoint] = useState<null | SeriesPointXY>(null);
  const { series, seriesMax, yMax, yMin } = getSeries(dataOverTime[0]);
  const getNearestPoint = useGetNearestPoint(seriesMax, { axis: 'x' });

  const [[xLeft, xRight], setLocalHandlePositions] = useState([
    ticks.indexOf(dateLeft),
    ticks.indexOf(dateRight),
  ]);

  const onPointerMove = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point && point.x !== null) {
      const nearestPoint = getNearestPoint(point);

      if (nearestPoint && nearestPoint.x !== null) {
        setFocusPoint(nearestPoint);
        return;
      }
    }

    setFocusPoint(null);
  };

  const handleDateRangeChange = (nextXLeft: number, nextXRight: number) => {
    setLocalHandlePositions([nextXLeft, nextXRight]);
  };

  const handleDateRangeApply = (nextXLeft: number, nextXRight: number) => {
    handleDateRangeChange(nextXLeft, nextXRight);

    if (onDateRangeChange) {
      if (ticks[nextXLeft] !== dateLeft || ticks[nextXRight] !== dateRight) {
        onDateRangeChange(ticks[nextXLeft], ticks[nextXRight]);
      }
    }
  };

  useEffect(() => {
    setLocalHandlePositions([ticks.indexOf(dateLeft), ticks.indexOf(dateRight)]);
  }, [dateLeft, dateRight, ticks]);

  return (
    <Flex container>
      <Viz
        height={height}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerMove}
        xDomain={ticks.length ? ticks.map((_, x) => x) : [0, 1, 2]}
        xDomainScale="band"
        xDomainPaddingOuter={0.5}
        yDomain={[yMin, yMax]}
      >
        {withBackground && (
          <VizHTML>
            <VizHTMLArtboard>
              <Base borderRadius="x2" backgroundColor="gray-1" width="100%" height="100%" />
            </VizHTMLArtboard>
          </VizHTML>
        )}

        <VizSVG offsetArtboardClipMaskTop={4}>
          {metrics.map((metric, i) =>
            series[i].map(
              ({ x, y }) =>
                y !== null && (
                  <VizSVGLine
                    key={`${metric}-${x}`}
                    animate
                    clipToArtboard
                    originY={0}
                    series={[
                      { x, y: 0 },
                      { x, y: y || 0 },
                    ]}
                    stroke={
                      x !== null && x >= xLeft && x <= xRight
                        ? BUILD_METRIC_COLORS_HEX[metric]
                        : variables.colorGray3
                    }
                    strokeLinecap={isLastValue(series, i, x) ? 'round' : undefined}
                    strokeWidth={8}
                  />
                ),
            ),
          )}
        </VizSVG>

        <VizHTML>
          <VizHTMLArtboard>
            <MessageFetching fetching={isFetching} />
            <MessageFailing failing={isError} onFetch={refetch} />
          </VizHTMLArtboard>

          {withTimeSelector && (
            <BuildTimeSelector
              onApply={handleDateRangeApply}
              onChange={handleDateRangeChange}
              xLeft={xLeft}
              xRight={xRight}
            />
          )}
        </VizHTML>

        <BuildTooltip point={focusPoint}>
          {focusPoint?.x !== null &&
            focusPoint?.x !== undefined &&
            dataOverTime[0] &&
            ticks[focusPoint.x] && (
              <BuildTimesByStatusTooltipContent
                dataOverTime={dataOverTime[0]}
                step={step}
                stepSize={stepSize}
                timestamp={ticks[focusPoint.x]}
                x={focusPoint.x}
              />
            )}
        </BuildTooltip>
      </Viz>
    </Flex>
  );
};

export default BuildTimesByStatus;
